import React, { useState, useEffect } from 'react';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';

const QuestionModal = ({
  show,
  onClose,
  onHide,
  onSave,
  onSuccess,
  question = null,
  levels: initialLevels = [],
}) => {
  const handleModalClose = onClose || onHide || (() => {});
  const handleModalSave = onSave || onSuccess || (() => {});

  const [stem, setStem] = useState('');
  const [levelId, setLevelId] = useState(1);
  const [lessonId, setLessonId] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [explanation, setExplanation] = useState('');
  const [useIn, setUseIn] = useState('both');
  const [status, setStatus] = useState('published');
  const [levels, setLevels] = useState(initialLevels);
  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch levels if not provided
  useEffect(() => {
    if (show && (!initialLevels || initialLevels.length === 0)) {
      streetlearnApi.getLevels().then((res) => setLevels(res || [])).catch(() => {});
    } else if (initialLevels && initialLevels.length > 0) {
      setLevels(initialLevels);
    }
  }, [show, initialLevels]);

  useEffect(() => {
    if (question) {
      setStem(question.stem || '');
      setLevelId(question.level_id || 1);
      setLessonId(question.lesson_id || '');
      setOptions(
        Array.isArray(question.options) && question.options.length === 4
          ? question.options
          : ['', '', '', '']
      );
      setCorrectIndex(question.correct_index ?? 0);
      setExplanation(question.explanation || '');
      setUseIn(question.use_in || 'both');
      setStatus(question.status || 'published');
    } else {
      setStem('');
      setLevelId(1);
      setLessonId('');
      setOptions(['', '', '', '']);
      setCorrectIndex(0);
      setExplanation('');
      setUseIn('both');
      setStatus('published');
    }
  }, [question, show]);

  // Load lessons when levelId changes
  useEffect(() => {
    if (show && levelId) {
      setLoadingLessons(true);
      streetlearnApi
        .getLessons({ level_id: levelId })
        .then((res) => setLessons(res || []))
        .catch((err) => console.error('Failed to load lessons for level:', err))
        .finally(() => setLoadingLessons(false));
    }
  }, [levelId, show]);

  if (!show) return null;

  const handleOptionChange = (idx, value) => {
    const next = [...options];
    next[idx] = value;
    setOptions(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stem.trim()) {
      showToast.warning('Please provide a question stem.');
      return;
    }
    for (let i = 0; i < 4; i++) {
      if (!options[i] || !options[i].trim()) {
        showToast.warning(`Option ${String.fromCharCode(65 + i)} cannot be empty.`);
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        stem: stem.trim(),
        level_id: Number(levelId),
        lesson_id: lessonId ? Number(lessonId) : null,
        options: options.map((o) => o.trim()),
        correct_index: Number(correctIndex),
        explanation: explanation.trim() || undefined,
        use_in: useIn,
        status,
      };

      if (question?.id) {
        await streetlearnApi.updateQuestion(question.id, payload);
        showToast.success('Question updated successfully!');
      } else {
        await streetlearnApi.createQuestion(payload);
        showToast.success('Question created successfully!');
      }
      handleModalSave();
      handleModalClose();
    } catch (err) {
      console.error(err);
      showToast.error(err?.response?.data?.message || 'Failed to save question.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="modal-title font-bold m-0">
                {question ? `Edit Question #${question.id}` : 'Create New Question'}
              </h5>
              <button
                type="button"
                className="close"
                style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                onClick={handleModalClose}
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {/* Stem */}
              <div className="form-group mb-3">
                <label className="font-semibold text-sm">
                  Question Stem (Text) <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={stem}
                  onChange={(e) => setStem(e.target.value)}
                  placeholder="Enter the question text..."
                  required
                />
              </div>

              {/* Level & Lesson */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="font-semibold text-sm">
                    Level <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={levelId}
                    onChange={(e) => {
                      setLevelId(e.target.value);
                      setLessonId('');
                    }}
                    required
                  >
                    {levels.map((lvl) => (
                      <option key={lvl.id} value={lvl.id}>
                        Level {lvl.number}: {lvl.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="font-semibold text-sm">Associated Lesson</label>
                  <select
                    className="form-control"
                    value={lessonId}
                    onChange={(e) => setLessonId(e.target.value)}
                    disabled={loadingLessons}
                  >
                    <option value="">-- Exam Only / Level General --</option>
                    {lessons.map((les) => (
                      <option key={les.id} value={les.id}>
                        {les.code} - {les.title}
                      </option>
                    ))}
                  </select>
                  {loadingLessons && <small className="text-muted">Loading lessons...</small>}
                </div>
              </div>

              {/* 4 Options */}
              <label className="font-semibold text-sm mb-2">
                Options & Correct Answer <span className="text-danger">*</span>
              </label>
              <div className="mb-3">
                {['A', 'B', 'C', 'D'].map((letter, idx) => (
                  <div className="input-group mb-2" key={letter}>
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <input
                          type="radio"
                          name="correct_index"
                          checked={correctIndex === idx}
                          onChange={() => setCorrectIndex(idx)}
                          title={`Select option ${letter} as correct`}
                          className="mr-2"
                        />
                        <strong>Option {letter}</strong>
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      value={options[idx]}
                      onChange={(e) => handleOptionChange(idx, e.target.value)}
                      placeholder={`Choice ${letter}`}
                      required
                    />
                  </div>
                ))}
                <small className="text-muted">Select the radio button next to the correct answer.</small>
              </div>

              {/* Explanation */}
              <div className="form-group mb-3">
                <label className="font-semibold text-sm">Explanation / Rationale</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  placeholder="Why is this answer correct? (Shown during quiz reviews)"
                />
              </div>

              {/* Usage & Status */}
              <div className="row mb-2">
                <div className="col-md-6">
                  <label className="font-semibold text-sm">Use In</label>
                  <select
                    className="form-control"
                    value={useIn}
                    onChange={(e) => setUseIn(e.target.value)}
                  >
                    <option value="both">Both (Quiz & Exam)</option>
                    <option value="quiz">Lesson Quiz Only</option>
                    <option value="exam">Level Exam Only</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="font-semibold text-sm">Status</label>
                  <select
                    className="form-control"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-default btn-sm" onClick={handleModalClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Saving...' : question ? 'Update Question' : 'Create Question'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuestionModal;
